import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle, ArrowRight, MessageCircle } from "lucide-react";

// FAQ content lives in src/seoData.js so the page, FAQPage schema, llms.txt and the
// pre-rendered HTML always stay identical.
import { FAQS as FAQ_DATA } from "../seoData";
export const FAQS = FAQ_DATA;

// Decorative concentric rings for the background (rendered once)
const Rings = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 600 600"
    className="pointer-events-none absolute -right-48 -top-24 -z-10 h-[640px] w-[640px] text-[#702cb0] sm:-right-32 lg:-right-20"
    fill="none"
    stroke="currentColor"
  >
    {[90, 150, 210, 270, 330].map((r, i) => (
      <circle key={r} cx="300" cy="300" r={r} strokeOpacity={0.16 - i * 0.025} strokeWidth="1.2" />
    ))}
    <circle cx="300" cy="210" r="5" fill="#12dfbd" stroke="none" opacity=".8" />
    <circle cx="510" cy="300" r="4" fill="#702cb0" stroke="none" opacity=".5" />
    <circle cx="150" cy="430" r="4" fill="#12dfbd" stroke="none" opacity=".6" />
  </svg>
);

const FAQSection = () => {
  // First question open by default so the section never looks empty
  const [openIndexes, setOpenIndexes] = useState([0]);

  const toggleFAQ = (index) =>
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );

  return (
    <section
      id="faq"
      aria-labelledby="faq-heading"
      className="relative isolate w-full overflow-hidden bg-gradient-to-br from-[#fbf7ff] via-white to-[#effbf8] py-14 font-sans text-[#28194f] sm:py-16 lg:py-24"
    >
      {/* Concentric rings + soft teal glow: a different look from the other sections */}
      <Rings />
      <div className="pointer-events-none absolute -bottom-24 -left-24 -z-10 h-96 w-96 rounded-full bg-[#12dfbd]/15 blur-3xl" />

      <div className="mx-auto grid w-full max-w-[1300px] items-start gap-10 px-4 sm:px-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14 lg:px-14">
        {/* ===== Left: intro panel (sticky on desktop) ===== */}
        <div className="lg:sticky lg:top-24">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#28194f] via-[#48247f] to-[#702cb0] p-6 text-white shadow-[0_24px_60px_-20px_rgba(72,36,127,0.55)] sm:p-10">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-fuchsia-400/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-indigo-400/20 blur-3xl" />

            <div className="relative">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-[#12dfbd] backdrop-blur-md">
                <HelpCircle className="h-3.5 w-3.5" />
                FAQ
              </div>

              <h2
                id="faq-heading"
                className="text-balance text-[26px] font-light leading-[1.25] tracking-tight sm:text-[34px] lg:text-[36px] xl:text-[40px]"
              >
                Frequently Asked{" "}
                <span className="bg-gradient-to-r from-[#12dfbd] to-[#c9b5ff] bg-clip-text font-normal text-transparent">
                  Questions
                </span>
              </h2>

              <p className="mt-4 text-sm font-light leading-[1.8] text-white/90 sm:text-base">
                Everything you need to know about our lead management solution.
                Can&apos;t find what you&apos;re looking for? Our team is happy
                to help.
              </p>

              <div className="mt-6 h-1 w-20 rounded-full bg-gradient-to-r from-[#12dfbd] via-[#c9b5ff] to-[#702cb0]" />

              {/* Contact card */}
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-md">
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-[#12dfbd]">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-base font-medium">Still have questions?</p>
                    <p className="text-xs font-light text-white/80">
                      We usually reply within one business day.
                    </p>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="group inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-[#12dfbd] px-6 py-3 text-xs font-semibold text-[#102b37] shadow-lg shadow-[#12dfbd]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#40f0d3] hover:shadow-[#12dfbd]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:text-sm"
                >
                  Contact Our Team
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Right: accordion ===== */}
        <div className="space-y-4">
          {FAQS.map((faq, index) => {
            const open = openIndexes.includes(index);
            return (
              <div
                key={faq.question}
                className={`group relative overflow-hidden rounded-2xl border bg-white transition-all duration-300 ${
                  open
                    ? "border-[#702cb0]/40 shadow-[0_20px_40px_-16px_rgba(72,36,127,0.35)]"
                    : "border-[#48247f]/10 shadow-[0_8px_30px_-14px_rgba(72,36,127,0.25)] hover:-translate-y-0.5 hover:border-[#702cb0]/30"
                }`}
              >
                {/* Accent bar on the left when open */}
                <div
                  className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#12dfbd] via-[#702cb0] to-[#48247f] transition-opacity duration-300 ${
                    open ? "opacity-100" : "opacity-0"
                  }`}
                />

                <h3>
                  <button
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={open}
                    aria-controls={`faq-panel-${index}`}
                    id={`faq-button-${index}`}
                    className="flex w-full items-center gap-4 min-h-[64px] px-4 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#702cb0] sm:px-6"
                  >
                    {/* Number badge */}
                    <span
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-xs font-semibold transition-all duration-300 ${
                        open
                          ? "bg-gradient-to-br from-[#48247f] to-[#702cb0] text-white"
                          : "bg-[#f1ecfa] text-[#48247f] group-hover:bg-[#e6dcf7]"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span
                      className={`flex-1 text-[15px] font-medium leading-snug transition-colors duration-200 sm:text-base ${
                        open ? "text-[#48247f]" : "text-[#28194f]"
                      }`}
                    >
                      {faq.question}
                    </span>

                    <span
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                        open
                          ? "rotate-180 bg-[#12dfbd] text-[#102b37]"
                          : "bg-[#f1ecfa] text-[#48247f] group-hover:bg-[#e6dcf7]"
                      }`}
                    >
                      <ChevronDown className="h-5 w-5" />
                    </span>
                  </button>
                </h3>

                {/* Answer: animates height smoothly via grid rows (no max-height guess) */}
                <div
                  id={`faq-panel-${index}`}
                  role="region"
                  aria-labelledby={`faq-button-${index}`}
                  aria-hidden={!open}
                  className={`grid transition-all duration-500 ease-in-out ${
                    open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-6 pl-[4.25rem] text-sm leading-[1.75] text-slate-700 sm:px-6 sm:pb-6 sm:pl-[4.75rem] sm:text-[15px]">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;